import React, { useEffect, useId, useRef, useState } from "react";
import { ReadableNavButton } from "./Sticker";
import FormLabel from "./FormLabel";
import {
  WorkflowModalBody,
  WorkflowModalFooter,
  WorkflowModalHeader,
  WorkflowModalShell,
} from "./workflow/WorkflowModal";

/** Readable confirm for destructive or neutral host actions (dashboard, feed). */
export default function DangerConfirmModal({
  open,
  title,
  message,
  confirmLabel,
  onClose,
  onConfirm,
  busy,
  requireYes = false,
  showReasonField = false,
  reasonLabel = "Reason (shown to guests)",
  reasonPlaceholder = "e.g. Venue unavailable",
  tone = "destructive",
  testId,
}) {
  const dialogRef = useRef(null);
  const baseId = useId();
  const titleId = `${baseId}-title`;
  const messageId = `${baseId}-message`;
  const reasonFieldId = `${baseId}-reason`;
  const confirmFieldId = `${baseId}-confirm-yes`;

  const [confirmText, setConfirmText] = useState("");
  const [reasonText, setReasonText] = useState("");
  const canConfirm = !requireYes || confirmText.trim().toLowerCase() === "yes";
  const confirmVariant = tone === "neutral" ? "primary" : "danger";

  useEffect(() => {
    if (!open) {
      setConfirmText("");
      setReasonText("");
    }
  }, [open]);

  useEffect(() => {
    if (!open || busy) return undefined;
    const focusId = showReasonField ? reasonFieldId : requireYes ? confirmFieldId : null;
    if (!focusId) return undefined;
    const frame = requestAnimationFrame(() => {
      document.getElementById(focusId)?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [open, busy, showReasonField, requireYes, reasonFieldId, confirmFieldId]);

  function handleConfirm(e) {
    e?.preventDefault();
    if (busy || !canConfirm) return;
    if (showReasonField) {
      onConfirm(reasonText.trim() || null);
    } else {
      onConfirm();
    }
  }

  if (!open) return null;

  return (
    <WorkflowModalShell
      dialogRef={dialogRef}
      onClose={onClose}
      onBackdropClose={busy ? undefined : onClose}
      testId={testId}
      titleId={titleId}
      describedById={messageId}
      busy={busy}
      maxWidthClass="sm:max-w-[440px]"
    >
      <form onSubmit={handleConfirm}>
        <WorkflowModalHeader
          title={title}
          titleId={titleId}
          onClose={busy ? undefined : onClose}
          closeTestId="danger-modal-close"
        />
        <WorkflowModalBody>
          <p
            id={messageId}
            className="font-ui font-medium text-[15px] text-ink/80 leading-relaxed text-pretty"
          >
            {message}
          </p>
          {showReasonField && (
            <div>
              <FormLabel as="label" htmlFor={reasonFieldId} className="block">
                {reasonLabel}{" "}
                <span className="font-normal text-ink/70">(optional)</span>
              </FormLabel>
              <input
                id={reasonFieldId}
                className="field mt-2"
                value={reasonText}
                onChange={(e) => setReasonText(e.target.value)}
                placeholder={reasonPlaceholder}
                autoComplete="off"
                data-testid="danger-modal-reason-input"
              />
            </div>
          )}
          {requireYes && (
            <div>
              <FormLabel as="label" htmlFor={confirmFieldId} className="block">
                Type <span className="font-semibold">yes</span> to confirm
              </FormLabel>
              <input
                id={confirmFieldId}
                className="field mt-2"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="yes"
                autoComplete="off"
                inputMode="text"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                data-testid="delete-confirm-input"
              />
            </div>
          )}
        </WorkflowModalBody>
        <WorkflowModalFooter safeArea className="flex flex-col gap-2 sm:flex-row sm:justify-end">
          <ReadableNavButton
            type="button"
            onClick={onClose}
            disabled={busy}
            className="w-full sm:w-auto"
            data-testid="danger-modal-cancel"
          >
            Cancel
          </ReadableNavButton>
          <ReadableNavButton
            type="submit"
            variant={confirmVariant}
            disabled={busy || !canConfirm}
            className="w-full sm:w-auto"
            data-testid="danger-modal-confirm"
          >
            {busy ? "Working…" : confirmLabel}
          </ReadableNavButton>
        </WorkflowModalFooter>
      </form>
    </WorkflowModalShell>
  );
}
